import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Household } from '../../../../common/src/model.ts';
import { OnboardingPages } from '../../../../common/src/pages.ts';
import * as server_api from '../../../../common/src/server_api.ts';
import { localStorageGet, LocalStorageKey, localStorageSet } from '../../data/client_local_storage.ts';
import { synchronizeOrgsWithServer, useCurrentOrg } from '../../data/use_current_org.tsx';
import { useLocalStorage } from '../../data/use_local_storage.tsx';
import { alert } from '../../util/confirm.tsx';
import { Button } from './button.tsx';
import {
  BoldMessage,
  getOnboardingContext,
  goToNextOnboardingStep,
  goToOnboardingDestination,
  isAdminInvite,
  Message,
} from './common.tsx';
import { Frame } from './frame.tsx';
import { nextOnboardingStep } from './onboarding_flow.ts';

// Creates the household from the onboarding last name and email and goes on to the next step.
// Shared by the create confirmation page and, for a promotional campaign's visitor who confirmed
// their email earlier, the last-name page (GitHub issue #3551).
export function useCreateHousehold() {
  const [onboardingOrg] = useLocalStorage(LocalStorageKey.OnboardingOrg);
  const { setHouseholdOrg } = useCurrentOrg();
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  // ref rather than state so a second tap during the same render cycle is also blocked
  const isCreatingRef = useRef(false);
  const onCreateHousehold = useCallback(async () => {
    if (isCreatingRef.current) {
      return;
    }
    // read at call time: the last-name page creates right after storing the name
    const familyLastName = localStorageGet(LocalStorageKey.OnboardingLastName);
    const householdEmail = localStorageGet(LocalStorageKey.OnboardingHouseholdEmail);
    if (!familyLastName || !householdEmail) {
      await alert({ confirmation: <span>Some information is missing</span> });
      await goToNextOnboardingStep(navigate, { from: OnboardingPages.HouseholdCreate, action: 'missingInformation' });
      return;
    }
    isCreatingRef.current = true;
    setIsCreating(true);
    try {
      let household: Household;
      try {
        household = await server_api.createHousehold({ householdEmail, familyLastName });
      } catch {
        await alert({ confirmation: <span>An error occurred creating household</span> });
        await goToNextOnboardingStep(navigate, { from: OnboardingPages.HouseholdCreate, action: 'createFailed' });
        return;
      }
      localStorageSet(LocalStorageKey.Household, household);
      const next = nextOnboardingStep(
        { from: OnboardingPages.HouseholdCreate, action: 'created' },
        getOnboardingContext(),
      );
      // an admin invite sets the household's org itself, from the invite
      if (onboardingOrg && !isAdminInvite()) {
        await setHouseholdOrg(onboardingOrg);
      }
      // the admin invite page syncs the orgs itself; every other path continues in onboarding
      if (next.kind === 'onboarding') {
        await synchronizeOrgsWithServer({ force: true });
      }
      await goToOnboardingDestination(navigate, next);
    } finally {
      isCreatingRef.current = false;
      setIsCreating(false);
    }
  }, [navigate, onboardingOrg, setHouseholdOrg]);

  return { createHousehold: onCreateHousehold, isCreating };
}

export const HouseholdCreate = () => {
  const [familyLastName] = useLocalStorage(LocalStorageKey.OnboardingLastName);
  const [householdEmail] = useLocalStorage(LocalStorageKey.OnboardingHouseholdEmail);
  const navigate = useNavigate();
  const { createHousehold: onCreateHousehold, isCreating } = useCreateHousehold();

  const onEditInfo = useCallback(async () => {
    await goToNextOnboardingStep(navigate, { from: OnboardingPages.HouseholdCreate, action: 'editInformation' });
  }, [navigate]);

  return (
    <Frame
      onboardingPage={OnboardingPages.HouseholdCreate}
      mainContent={
        <Message $widthInChars={24}>
          Would you like to create a new <BoldMessage>{familyLastName}</BoldMessage> household with email{' '}
          <BoldMessage>{householdEmail}</BoldMessage>?
        </Message>
      }
      footerContent={
        <>
          <Button onClick={onCreateHousehold} busy={isCreating}>
            CREATE HOUSEHOLD
          </Button>
          <Button onClick={onEditInfo}>EDIT INFORMATION</Button>
        </>
      }
      onEnterKeypress={onCreateHousehold}
    />
  );
};
